import "./style.scss";

import { Breadcrumbs, CrumbClickFunc } from "@amzn/stencil-react-components/breadcrumbs";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor, ChipIconPosition, ChipSize } from "@amzn/stencil-react-components/chip";
import { Col, Container, Row, View } from "@amzn/stencil-react-components/layout";
import { Hr } from "@amzn/stencil-react-components/layout";
import { WithModal } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { TabBar, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { P, Text } from "@amzn/stencil-react-components/text";
import {
  IconAccessibilityPersonCircleExtraSmall,
  IconBookmarkFillExtraSmall,
  IconDesktopExtraSmall,
  IconMessageSmall,
} from "@amzn/stencil-react-icons";
import { IconSize } from "@amzn/stencil-react-icons/icons/interfaces";
import { useLists } from "common/context/lists/ListsContext";
import { checkOsCompatibility, isParentChildProduct } from "common/helpers";
import { handleOrderTeam as handleOrderTeamHelper } from "common/helpers/handleOrderTeam";
import { StringHelper } from "common/helpers/string-helper";
import { createItemTaxonomy } from "common/helpers/taxonomy-helper";
import { Paths } from "common/paths/paths";
import { PreCheckoutInformation } from "common/types";
import { PERMISSIONS } from "config";
import { useAssetReturns } from "hooks";
import { useProductFeedback } from "hooks/useProductFeedback";
import parse from "html-react-parser";
import NoLogoImage from "img/no_logo_image.png";
import { RuleProperties } from "json-rules-engine";
import { getUserOS } from "lib/helpers";
import { getItemRules } from "lib/helpers/rules-engine-factory";
import { UserPermissions } from "lib/services";
import { AppSenseService, CheckLicenseCapOutput } from "lib/services/app-sense-service";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import { IItemVersion, ItemDetails, ItemSubtypes, ItemTypes } from "lib/types/items";
import { capitalize, forEach } from "lodash";
import moment from "moment";
import React, { FunctionComponent, ReactElement, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { CheckoutState } from "view/Page/CheckoutPage/CheckoutPage";

import { getString } from "../../uistringlabels/uiStringUtils";
import {
  CFMModuleName,
  CustomerFeedback,
  CustomerFeedbackAlignment,
  CustomerFeedbackIconPosition,
  CustomerFeedbackMargins,
} from "../CustomerFeedback";
import { EtaStatusIndicator } from "../EtaStatusIndicator/EtaStatusIndicator";
import { FoundationalChip } from "../FoundationalChip";
import licenses from "../LicenseSelect/figma-licenses.json";
import { License, LicenseSelect } from "../LicenseSelect/LicenseSelect";
import { ListActions } from "../ListActions/ListActions";
import { useMessageBannersBox } from "../MessageBannersBox/hook";
import { OrderNow } from "../OrderNow";
import { PageMainContainer } from "../PageMainContainer/PageMainContainer";
import { ProductDetailsBanners } from "../ProductDetailsBanner";
import { ProductReviewsList } from "../ProductReviews";
import { StarAverageRating, StarAverageRatingSize, StarDetailedRating, StarDetailedRatingSize } from "../Ratings";
import { RecommendedProducts } from "../RecommendedProducts";
import { SelectChildSku, VersionObject } from "../SelectChildSku";
import { SubmitOrderModal } from "../SubmitOrderModal";
import { SubmitReturnModal } from "../SubmitReturnModal";
import { SubmitReviewForm } from "../SubmitReviewForm";
import { useProductDetailsPage } from "./useProductDetailsPage";

// Constant to validate if the PDP is for a figma product
const FIGMA = "figma";

const SECURITY = "security";
const NA_STRING = "N/A";

const breadCrumbsMapping: { [key in ItemTypes]: string } = {
  [ItemTypes.SOFTWARE]: "/",
  [ItemTypes.HARDWARE]: Paths.hardwareSearch.url,
};

export interface ProductDetailsPageProps {
  /**
   * If it is a software or hardware item, determines OrderNow button behavior
   */
  assetType: ItemTypes;

  /**
   * Item to render
   */
  item: ItemDetails;

  /**
   * Same as parent item info if the item does not have children or versions
   */
  defaultVersion: IItemVersion | undefined;

  /**
   * Empty if the item does not have children or versions
   */
  versions: IItemVersion[];

  /**
   * Updates the default version used to make the order
   * @param version The selected version in the radio/select
   */
  updateVersionSelected: (version: any) => void;
  preCheckoutInformation?: PreCheckoutInformation;
  fetchPreCheckoutData?: (token: string) => void;
  userVote?: FeedbackModelRecord;
}

export const ProductDetailsPage: FunctionComponent<ProductDetailsPageProps> = (props: ProductDetailsPageProps) => {
  // Check if software reviews are enabled
  const areSoftwareReviewsEnabled = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_SOFTWARE_REVIEWS);

  // Tab titles
  const aboutTabTitle = getString("details.tabs.about") || "About";
  const reviewsTabTitle = getString("details.tabs.reviews") || "Reviews";
  const tabs = [
    { value: aboutTabTitle, label: aboutTabTitle },
    ...(props.assetType === ItemTypes.SOFTWARE && areSoftwareReviewsEnabled
      ? [{ value: reviewsTabTitle, label: reviewsTabTitle }]
      : []), //only add tab when item is type software
  ];
  const taxonomyIdItem = createItemTaxonomy(props.item.taxonomyId);
  // Use the explicit reviewTaxonomyId from the data layer (set to the parent product by default).
  // This keeps the decision about which product owns the reviews in the data mapping,
  // so it can be changed without touching the UI component.
  const reviewTaxonomyIdItem = createItemTaxonomy(props.item.reviewTaxonomyId ?? props.item.taxonomyId);
  const appSenseService = new AppSenseService();
  const userOS: string[] = getUserOS();
  const history = useHistory();
  const { addErrorBanner } = useMessageBannersBox();

  const domain = props.assetType === ItemTypes.SOFTWARE ? "software" : "amznham";

  const isOsCompatible = checkOsCompatibility(
    props.item.os ?? [""],
    userOS,
    props.assetType,
    props.item.subtype as ItemSubtypes | undefined
  );
  const [selectedVersion, setSelectedVersion] = useState(props.defaultVersion ?? undefined);

  // Sync selectedVersion when defaultVersion changes (e.g., after async version filtering)
  useEffect(() => {
    if (props.defaultVersion) {
      setSelectedVersion(props.defaultVersion);
    }
  }, [props.defaultVersion]);
  const [isPriceOverwritten, setIsPriceOverwritten] = useState<boolean>(props.item.taxonomyId!.includes(FIGMA));
  const [amount, setAmount] = useState(isPriceOverwritten ? licenses[0].price : props.defaultVersion?.amount);
  const [licenseSelection, setLicenseSelection] = useState<License | null>(isPriceOverwritten ? licenses[0] : null);
  const [limitCap, setLimitCap] = useState<CheckLicenseCapOutput>({} as CheckLicenseCapOutput);
  const [hasOrderedItem, setHasOrderedItem] = useState<string | undefined>();
  const [isLicenseCapLoading, setIsLicenseCapLoading] = useState<boolean>(false);
  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs });
  const [openFlyout, setOpenFlyout] = useState(false);
  const [resources, setResources] = useState(false);
  const [support, setSupport] = useState(false);
  const [rating, setRating] = useState(props.userVote?.rating ?? 0);
  const [reviewsKey, setReviewsKey] = useState(0); // used to refresh reviews on post
  const [hasRecommendedProducts, setHasRecommendedProducts] = useState(false);

  const { order, activeOrder, loadingOrders } = useProductDetailsPage(props.item.taxonomyId, selectedVersion);
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);

  const { yourLists, getYourLists } = useLists();

  useEffect(() => {
    if (toggleLists) {
      getYourLists({
        allowedDomains: ["software"],
      });
    }
  }, [toggleLists]);

  const isFoundational = props.item.itptClassification == "Foundational";

  const {
    canReturn,
    returnAsset,
    isLoading: returnLoading,
    effectiveHasLicense,
    inProgress,
    returnRequests,
  } = useAssetReturns({
    itemId: selectedVersion?.taxonomyId || props.item.taxonomyId,
    productName: selectedVersion?.applinkProductName || props.item.applinkProductName,
    instanceName: selectedVersion?.applinkInstanceName || props.item.applinkInstanceName,
    skipValidation: !(selectedVersion?.isReturnable ?? props.item.isReturnable),
  });

  const assetStatus = {
    hasLicense: effectiveHasLicense,
    returnStatus: {
      inProgress,
      returnRequests,
    },
  };

  const {
    isLoading: feedbackLoading,
    itemFeedbackStats,
    doPostFeedback,
    setItemFeedbackStats,
  } = useProductFeedback({
    domain,
    defaultValues: {
      itemFeedbackStats: props.item.feedback,
    },
  });

  const isLoadingAssetReturnStatus = returnLoading;

  // Parent-Child product detection and team access functionality
  const isParentChild = isParentChildProduct(props.versions);

  // Extract item rules from the selected version or parent item for OrderNow component
  const itemRules: RuleProperties[] = useMemo(() => {
    return selectedVersion ? getItemRules(selectedVersion) : getItemRules(props.item);
  }, [selectedVersion, props.item]);

  // Reactive calculation of productDownloadUrl that updates when selectedVersion changes
  const productDownloadUrlValue = useMemo(() => {
    return selectedVersion?.productDownloadUrl ?? props.item.productDownloadUrl;
  }, [selectedVersion, props.item.productDownloadUrl]);

  /**
   * Handles team access order requests for parent-child products
   * Uses the shared helper function to convert the selected team back to IItemVersion format
   * and updates the component state to trigger the standard order flow for the selected team
   *
   * @param selectedTeam - The team/version object selected by the user from the team access modal
   */
  const handleOrderTeam = (selectedTeam: VersionObject): void => {
    handleOrderTeamHelper(selectedTeam, props.versions, setSelectedVersion, props.updateVersionSelected);
  };

  const displayedItemDescription =
    UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AUTO_GEN_FORMATTED_DESCRIPTION) &&
    props.item.autoGenFormattedDescription
      ? props.item.autoGenFormattedDescription
      : props.item.description;

  useEffect(() => {
    if (props.assetType === ItemTypes.HARDWARE) {
      handleChangeVersion(props.versions[0]);
    }
  }, []);

  useEffect(() => {
    checkPreviouslyOrder();
  }, [order]);

  useEffect(() => {
    if (!isPriceOverwritten) {
      setAmount(props.defaultVersion?.amount);
    }
  }, [props.defaultVersion]);

  useEffect(() => {
    checkLicenseCap(props.item.taxonomyId!, !props.item.requiresLicenseCapValidation!);
  }, [props.item.taxonomyId]);

  const checkPreviouslyOrder = async () => {
    if (order) {
      setHasOrderedItem(moment(order.requested?.on?.toString()).format("MM/DD/YYYY"));
    } else {
      setHasOrderedItem(undefined);
    }
  };

  const checkLicenseCap = async (taxonomyId: string, shouldSkipValidation: boolean) => {
    setIsLicenseCapLoading(true);
    try {
      const result: CheckLicenseCapOutput = await appSenseService.checkLicenseCap(taxonomyId, shouldSkipValidation);
      setLimitCap(result);
    } catch (error) {
      /**
       * possible errors:
       * 1. user alias not registered in AppSense table
       */
      // default values
      setLimitCap({
        isCapReached: false,
        closestLeaderInformation: {
          alias: "",
        },
      });
    } finally {
      setIsLicenseCapLoading(false);
    }
  };

  // This useEffect was moved up to be with the other useEffect hooks

  /* istanbul ignore next */
  const handleCrumbClick: CrumbClickFunc = (event, crumb) => {
    event.preventDefault();
    if (crumb.url) {
      history.push(crumb.url);
    }
  };

  /* istanbul ignore next */
  const handleChangeVersion = (newSelectedSku: any) => {
    setSelectedVersion(newSelectedSku);
    props.updateVersionSelected(newSelectedSku);
  };

  /**
   * Updates the selected license and related state values.
   *
   * @param {License | undefined} newLicense - The license to select, or undefined
   * @description When a license is provided, updates the amount to the license price,
   * marks the price as manually overwritten, and updates the license selection state.
   */
  const updateSelectedLicense = (newLicense?: License) => {
    if (newLicense) {
      setAmount(newLicense.price);
      setIsPriceOverwritten(true);
      setLicenseSelection(newLicense);
    }
  };

  if (!props.item) return <div>No item data</div>;

  const renderModal = ({ close }: { close: () => void }) => {
    return (
      <SubmitOrderModal
        close={close}
        preCheckoutData={props.preCheckoutInformation}
        fetchPreCheckoutData={props.fetchPreCheckoutData!}
        taxonomyId={props.item.taxonomyId!}
        name={props.item.name!}
        status={props.item.status}
        etaDays={props.item.etaDays}
        hasStock={props.item.hasStock}
        externalButtonText={props.item.externalButtonText}
        orderRedirectURL={props.item.orderRedirectUrl}
        orderWorkflow={props.item.orderWorkflow ?? ""}
        license={licenseSelection ?? undefined}
        isOsCompatible={isOsCompatible}
      />
    );
  };

  const renderFlyout = () => {
    if (!areSoftwareReviewsEnabled) return null;
    if (!reviewTaxonomyIdItem) return null;

    return (
      <SubmitReviewForm
        productName={props.item.parentName ?? props.item.name}
        titleText={"Submit review"}
        taxonomyId={reviewTaxonomyIdItem}
        domain={domain}
        isLoading={feedbackLoading}
        doPostFeedback={doPostFeedback}
        isOpen={openFlyout}
        setIsOpen={setOpenFlyout}
        previousRating={rating}
        setPreviousRating={setRating}
        setItemFeedback={setItemFeedbackStats}
        onReviewSubmitted={() => setReviewsKey(reviewsKey + 1)}
      />
    );
  };

  /* istanbul ignore next */
  const goToCheckoutPage = () => {
    const state = {
      parentName: props.item.parentName ?? props.item.name,
      parentTaxonomyId: props.item.parentTxId,
      defaultVersion: props.defaultVersion,
      versions: props.versions,
    } as CheckoutState;

    history.push({
      pathname: Paths.checkout.url,
      state,
    });
  };

  /**
   * Renders a modal dialog for confirming asset return
   * @param {Object} params - The parameters object
   * @param {() => void} params.close - Function to close the modal
   * @returns {JSX.Element} The rendered modal component
   */
  const renderReturnModal = ({ close }: { close: () => void }) => {
    const handleReturnConfirm = async () => {
      try {
        await returnAsset();
      } catch {
        addErrorBanner(getString("licenseReturn.returnError"));
        close();
      }
    };

    return (
      <SubmitReturnModal
        close={close}
        productName={props.item.name}
        onConfirm={handleReturnConfirm}
        isLoading={isLoadingAssetReturnStatus}
      />
    );
  };

  /**
   * Renders a button that triggers the return asset modal
   * Uses WithModal component to handle modal display logic
   * @returns {JSX.Element} A button wrapped in WithModal component
   */
  const displayReturnButton = () => {
    const hasReturnInProcess = assetStatus?.returnStatus?.inProgress;
    const buttonText = hasReturnInProcess
      ? getString("licenseReturn.pendingReturnButton")
      : getString("licenseReturn.returnButton");

    return (
      <WithModal renderModal={renderReturnModal}>
        {({ open }: { open: () => void }) => (
          <div className="return-button-container">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={open}
              data-cy="return-asset-button"
              data-testid="return-asset-button"
              icon={isLoadingAssetReturnStatus ? <Spinner size={SpinnerSize.Small} /> : undefined}
              disabled={isLoadingAssetReturnStatus || !canReturn}
            >
              {buttonText}
            </Button>
          </div>
        )}
      </WithModal>
    );
  };

  const renderCFM = () => {
    const module =
      props.assetType === ItemTypes.HARDWARE ? CFMModuleName.hardwareProductDetails : CFMModuleName.productDetails;

    return (
      <CustomerFeedback
        moduleName={module}
        alignment={CustomerFeedbackAlignment.Left}
        position={CustomerFeedbackIconPosition.Left}
      />
    );
  };

  const renderBreadcrumbs = () => {
    const parentItemName = props.item.parentName ?? props.item.name;
    return (
      <PageMainContainer>
        <Breadcrumbs
          crumbs={[
            { title: "Home", url: "/" },
            { title: capitalize(props.assetType), url: breadCrumbsMapping[props.assetType] },
            { title: parentItemName },
          ]}
          onCrumbClick={handleCrumbClick}
        />
      </PageMainContainer>
    );
  };

  const renderProductImage = () => (
    <Col className="pdp-column-left">
      <div className="product-img-wrapper">
        <img
          data-details-logo="details-logo"
          className="product-img"
          src={props.item.logoUrl.trim() !== "" ? props.item.logoUrl : NoLogoImage}
          alt={`${props.item.name} logo`}
        />
      </div>
    </Col>
  );

  const renderProductHeader = () => {
    const parentItemName = props.item.parentName ?? props.item.name;
    return (
      <Row justifyContent="space-between">
        <Row gridGap={"8px"} padding={{ top: "S200" }} alignItems="center">
          <Text fontSize="T200" className="product-title" data-cy="product-title">
            {parentItemName}
          </Text>
          {isFoundational && <FoundationalChip />}
          {toggleLists && (
            <ListActions
              taxonomyId={props.item.taxonomyId}
              lists={yourLists}
              allowedDomain="software"
              toolName={parentItemName}
            />
          )}
        </Row>
      </Row>
    );
  };

  // Use updated feedback stats after review submission, falling back to initial props
  const currentFeedback = itemFeedbackStats ?? props.item.feedback;

  const renderRatingsAndReviews = () => {
    if (!areSoftwareReviewsEnabled) return null;
    if (props.assetType !== ItemTypes.SOFTWARE) return null;
    return (
      <Row style={{ marginTop: "8px" }}>
        <StarAverageRating size={StarAverageRatingSize.Medium} averageRating={currentFeedback?.averageRating} />
        <Button
          variant={ButtonVariant.Tertiary}
          icon={<IconMessageSmall />}
          onClick={() => setOpenFlyout(true)}
          dataTestId="submit-review-button"
          style={{ fontSize: "14px" }}
        >
          Submit review
        </Button>
      </Row>
    );
  };

  const renderShortDescription = () =>
    props.item?.shortDescription && (
      <div className="product-short-details" data-cy="product-short-details">
        <Text data-details-description="details-description" fontSize="T100" className="product-description">
          {props.item.shortDescription}
        </Text>
      </div>
    );

  const renderChips = () => {
    const chips: ReactElement[] = [];

    if (props.item.isAccessible) {
      chips.push(
        <Chip
          icon={<IconAccessibilityPersonCircleExtraSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Blue}
          size={ChipSize.ExtraSmall}
          key="accesible"
        >
          Accessible
        </Chip>
      );
    }

    // category chips
    forEach(props.item.categories, (category) => {
      chips.push(
        <Chip
          icon={<IconBookmarkFillExtraSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Blue}
          size={ChipSize.ExtraSmall}
          key={`category-${category}`}
        >
          {category}
        </Chip>
      );
    });

    // os chips
    forEach(props.item.os, (os) => {
      chips.push(
        <Chip
          icon={<IconDesktopExtraSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Blue}
          size={ChipSize.ExtraSmall}
          key={`os-${os}`}
        >
          {os}
        </Chip>
      );
    });

    return (
      <Row gridGap="S200" style={{ marginTop: "24px", marginBottom: "24px" }}>
        {chips}
      </Row>
    );
  };

  const renderProductProperties = () => {
    // Check if user has permission to view compliance data
    const hasCompliancePermission = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_COMPLIANCE_INFORMATION);

    // Extract security compliance type information only if user has permission
    const securityCompliance = hasCompliancePermission
      ? props.item.complianceInformation?.complianceTypes?.find((type) => type.complianceType === SECURITY)
      : undefined;

    // Normalize the text for display (e.g., "HIGHLY_CONFIDENTIAL" -> "Highly confidential")
    const dataClassificationLevel = securityCompliance?.dataClassificationLevel
      ? StringHelper.normalizeText(securityCompliance.dataClassificationLevel)
      : NA_STRING;
    const securityStatus = securityCompliance?.complianceStatus
      ? StringHelper.normalizeText(securityCompliance.complianceStatus)
      : NA_STRING;

    return (
      <Row columnGap={60} gridGap={0} justifyContent="space-between" style={{ marginBottom: "24px" }}>
        <Text className="product-property">
          <label data-cy="delivery-time-title">{getString("details.deliveryTimeframe")}</label>
          <br />
          <EtaStatusIndicator
            etaDays={props.item.etaDays}
            hasStock={props.item.hasStock}
            size={IconSize.Small}
            status={props.item.status}
          />
        </Text>

        <Text className="product-property">
          <label data-cy="data-classification-title">{getString("details.dataClassification")}</label>
          <br />
          <strong>{dataClassificationLevel}</strong>
        </Text>

        <Text className="product-property">
          <label data-cy="security-status-title">{getString("details.securityStatus")}</label>
          <br />
          <strong>{securityStatus}</strong>
        </Text>

        <Text className="product-property">
          <label data-cy="version-title">{getString("details.version")}</label>
          <br />
          <strong>{props.item.version ?? NA_STRING}</strong>
        </Text>
      </Row>
    );
  };

  const renderVersionSelector = () => {
    if (props.versions.length > 0) {
      return (
        <Col data-testid="select-version-component" style={{ width: "100%" }}>
          <SelectChildSku
            itemChildVersions={props.versions}
            onChange={handleChangeVersion}
            selectedVersion={selectedVersion}
            autoSelectFirstVersion={false}
          />
        </Col>
      );
    }
  };

  const renderLicenseSelector = () => {
    if (isPriceOverwritten) {
      return (
        <Col data-testid="select-license-component">
          <LicenseSelect
            updateSelectedLicense={updateSelectedLicense}
            selectedLicense={licenseSelection}
            licenses={licenses}
          />
        </Col>
      );
    }
  };

  const renderOrderSection = ({ open }: { open: () => void }) => {
    const isL10MeetThreshold =
      limitCap.closestLeaderInformation &&
      limitCap.closestLeaderInformation.jobLevel &&
      limitCap.closestLeaderInformation.jobLevel >= 10
        ? true
        : false;

    return (
      <Col className="pdp-column-right" alignItems="flex-start">
        {props.defaultVersion && (
          <OrderNow
            orderWorkflow={props.item.orderWorkflow}
            externalButtonText={props.item.externalButtonText}
            itemType={props.assetType}
            orderNowHandler={open}
            goToCheckoutHandler={goToCheckoutPage}
            orderRedirectUrl={props.item.orderRedirectUrl || selectedVersion?.orderRedirectUrl || ""}
            moreDetailsUrl={props.item.moreDetailsUrl || ""}
            amount={amount}
            status={props.defaultVersion?.status}
            currency={props.defaultVersion?.currency}
            hasReachedCap={limitCap.isCapReached}
            isLoading={isLoadingAssetReturnStatus || loadingOrders || isLicenseCapLoading}
            activeOrder={!!activeOrder}
            selectVersionComponent={renderVersionSelector()}
            selectLicenseComponent={renderLicenseSelector()}
            // Add missing props from legacy version
            acme={props.item.acme}
            userOS={userOS}
            isReturnable={selectedVersion?.isReturnable ?? props.item.isReturnable}
            hasLicense={assetStatus?.hasLicense}
            isParentChildProduct={isParentChild}
            availableTeams={props.versions}
            onOrderTeam={handleOrderTeam}
            returnButtonComponent={displayReturnButton()}
            parentItemName={props.item.parentName ?? props.item.name}
            productDownloadUrl={productDownloadUrlValue}
            isL10ThresholdMeet={isL10MeetThreshold}
            wikiUrl={props.item.wikiUrl}
            itemRules={itemRules}
          />
        )}
      </Col>
    );
  };

  const renderReviewsSection = () => {
    return (
      <Col>
        <Row justifyContent="space-between" width={"40%"} margin={"16px"} style={{ paddingRight: "24px" }}>
          <View width={"50%"}>
            <StarDetailedRating
              size={StarDetailedRatingSize.Medium}
              averageRating={currentFeedback?.averageRating}
              voteNumber={currentFeedback?.numberOfRatings}
              ratingCounts={currentFeedback?.ratingCounts}
            />
          </View>
          <View display="flex" alignItems="flex-end" width={"30%"}>
            <Button
              variant={ButtonVariant.Primary}
              data-test-id="write-review-button"
              onClick={() => setOpenFlyout(true)}
            >
              {getString("productReviews.writeReview")}
            </Button>
          </View>
        </Row>
        <Row></Row>
        <ProductReviewsList domain={domain} taxonomyId={reviewTaxonomyIdItem!} refreshTrigger={reviewsKey} />
      </Col>
    );
  };

  const renderProductDetailsSection = ({ open }: { open: () => void }) => (
    <PageMainContainer className="product-details" paddingBottom="S500">
      <View>
        <Container paddingBottom="S300" paddingTop="S300">
          <ProductDetailsBanners
            limitCap={limitCap}
            assetStatus={assetStatus}
            isOsCompatible={isOsCompatible}
            hasOrderedItem={hasOrderedItem}
            productName={props.item.name}
          />
        </Container>
        <Row key={props.item.taxonomyId} gridGap="S700" alignItems="flex-start" className="product-description-box">
          {renderProductImage()}
          <Col className="pdp-column-center">
            {renderProductHeader()}
            {renderRatingsAndReviews()}
            {renderShortDescription()}
            {renderChips()}
            <hr />
            {renderProductProperties()}
          </Col>
          {renderOrderSection({ open })}
        </Row>
      </View>
    </PageMainContainer>
  );

  const renderAboutProductSection = () => (
    <PageMainContainer className="about-product-view" paddingBottom="S500" paddingTop="S500" paddingHorizontal="S300">
      <View style={{ marginBottom: "32px" }}>
        <TabBar {...tabBarProps} />
      </View>

      <TabSwitcher {...tabSwitcherProps}>
        <TabPanel value={getString("details.tabs.about")}>
          <View>
            <Row
              key={props.item.taxonomyId}
              gridGap="S700"
              alignItems="center"
              className="product-description-box"
              data-cy="product-description-box"
            >
              <View>
                <Text color={"neutral70"} fontSize={"T200"} fontWeight={"regular"}>
                  {displayedItemDescription && parse(displayedItemDescription)}
                </Text>
                <Hr color="neutral30" />
                {displayedItemDescription && renderCFM()}
              </View>
            </Row>
          </View>
        </TabPanel>

        {resources && (
          <TabPanel value={getString("details.tabs.resources") || "Resources"}>
            <Text>Resources content goes here</Text> {/* PENDING DATA FROM CATALOG */}
          </TabPanel>
        )}

        {support && (
          <TabPanel value={getString("details.tabs.support") || "Support"}>
            <Text>Support content goes here</Text> {/* PENDING DATA FROM CATALOG */}
          </TabPanel>
        )}

        {areSoftwareReviewsEnabled && (
          <TabPanel value={getString("details.tabs.reviews")}>{renderReviewsSection()}</TabPanel>
        )}
      </TabSwitcher>
    </PageMainContainer>
  );

  const renderRecommendedProductsSection = () => {
    // Only show for software items
    if (props.assetType !== ItemTypes.SOFTWARE) return null;

    // Only show if product has categories
    if (!props.item.categories || props.item.categories.length === 0) return null;

    return (
      <>
        <RecommendedProducts
          categories={props.item.categories}
          currentProductTaxonomyId={props.item.taxonomyId!}
          isCurrentProductFoundational={isFoundational}
          onProductsLoaded={setHasRecommendedProducts}
        />
        {hasRecommendedProducts && (
          <>
            <PageMainContainer paddingBottom="0" paddingTop="0">
              <Row className="recommended-products-cfm" alignItems="center" gridGap="S300">
                <Text
                  fontSize="T200"
                  fontWeight="regular"
                  color="neutral90"
                  style={{ whiteSpace: "nowrap", fontSize: "16px", lineHeight: "24px" }}
                >
                  {getString("customerFeedback.recommendedProducts")}
                </Text>
                <CustomerFeedback
                  moduleName={CFMModuleName.productDetails}
                  alignment={CustomerFeedbackAlignment.Left}
                  position={CustomerFeedbackIconPosition.Left}
                  margin={CustomerFeedbackMargins.None}
                />
              </Row>
            </PageMainContainer>
            <PageMainContainer style={{ paddingTop: "40px", paddingBottom: "0" }}>
              <Hr color="neutral30" />
            </PageMainContainer>
          </>
        )}
      </>
    );
  };

  return (
    <WithModal renderModal={renderModal}>
      {({ open }: { open: () => void }) => (
        <>
          {renderBreadcrumbs()}
          {renderProductDetailsSection({ open })}
          {renderRecommendedProductsSection()}
          {renderAboutProductSection()}
          {renderFlyout()}
        </>
      )}
    </WithModal>
  );
};
